import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

// Points a small "you have new notifications" tooltip at the bell icon.
// Same portal/spotlight technique as ParticipantOnboardingTour /
// CoachOnboardingTour, but the dismissal isn't permanent: it re-appears
// whenever the newest notification changes, not just once ever. Dismissal
// is recorded as "the id of the newest notification the participant has
// already been nudged about" rather than a flat seen/unseen boolean, so a
// fresh notification after the last dismissal makes it eligible again on
// the next login.
export const NOTIFICATION_TOOLTIP_STORAGE_PREFIX = 'viveka_notification_tooltip_seen_';
const STORAGE_PREFIX = NOTIFICATION_TOOLTIP_STORAGE_PREFIX;

const TOOLTIP_WIDTH = 260;
const VIEWPORT_MARGIN = 12;

const NotificationTooltip = ({ currentUser, unreadCount, newestNotificationId, anchorRef }) => {
  const userId = currentUser?.id?.uuid;
  const storageKey = userId ? `${STORAGE_PREFIX}${userId}` : null;

  const [dismissed, setDismissed] = useState(true);
  const [rect, setRect] = useState(null);

  const eligible = !!(unreadCount > 0 && newestNotificationId);

  // Re-check whenever the newest notification changes — if THIS one was
  // already nudged about, stay hidden; a newer one makes it eligible again.
  useEffect(() => {
    if (!storageKey || !eligible) {
      setDismissed(true);
      return;
    }
    setDismissed(localStorage.getItem(storageKey) === newestNotificationId);
  }, [storageKey, eligible, newestNotificationId]);

  const finish = () => {
    if (storageKey && newestNotificationId) {
      localStorage.setItem(storageKey, newestNotificationId);
    }
    setDismissed(true);
  };

  const show = eligible && !dismissed;

  useEffect(() => {
    if (!show) {
      setRect(null);
      return undefined;
    }
    const updateRect = () => {
      const el = anchorRef?.current;
      setRect(el ? el.getBoundingClientRect() : null);
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [show, anchorRef]);

  if (!show || !rect) return null;

  const top = rect.bottom + 12;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(rect.left - TOOLTIP_WIDTH + rect.width, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
  );

  return ReactDOM.createPortal(
    <>
      <style>{`
        @keyframes notifTooltipIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          borderRadius: '50%',
          boxShadow: '0 0 0 3px rgba(0, 115, 115, 0.55), 0 0 16px 2px rgba(20, 184, 166, 0.35)',
          pointerEvents: 'none',
          zIndex: 9998,
        }}
      />
      <div
        role="dialog"
        aria-modal="false"
        aria-label="New notifications"
        style={{
          position: 'fixed',
          top,
          left,
          width: `${TOOLTIP_WIDTH}px`,
          zIndex: 9999,
          background: '#FFFFFF',
          color: '#0F172A',
          borderRadius: '12px',
          padding: '14px 16px',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.35)',
          animation: 'notifTooltipIn 0.2s ease-out',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#064E3B' }}>
            You have new notifications
          </h3>
          <button
            type="button"
            onClick={finish}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              marginTop: '-2px',
              fontSize: '16px',
              lineHeight: 1,
              color: '#94A3B8',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, color: '#475569' }}>
          Click the bell to see what's new.
        </p>
      </div>
    </>,
    document.body
  );
};

export default NotificationTooltip;
